<template>
  <b-card border-variant="light" class="text-left position-sticky sticky-top">
    <h4>Filters</h4>
    <b-popover ref="popover" target="popover-button-event" title="Heads up!">
      You can search by either a job title or function but
      <strong>not both</strong>.
    </b-popover>

    <b-form-group>
      <p>
        <label>Product name</label>
        <b-form-input
          v-model="productName"
          placeholder="Product name..."
          @input="search"
        ></b-form-input>
      </p>
      <p>
        <label align="left" :v-bind:for="includeLocationId"></label>Regions
        <MultiTags
          v-model="includeLocationId"
          placeholder="Start typing a location..."
          main_name_key="canonical_name"
          secondary_name_key="fully_qualified_place_name"
          :autocomplete-endpoint="locationAutocompleteEndpoint"
          @input="search"
        ></MultiTags>
      </p>

      <!--      <p>-->
      <!--        <label align="left" :v-bind:for="exactLocationId"></label>Exact Location-->
      <!--        <MultiTags-->
      <!--          v-model="exactLocationId"-->
      <!--          placeholder="Start typing a location..."-->
      <!--          main_name_key="canonical_name"-->
      <!--          secondary_name_key="fully_qualified_place_name"-->
      <!--          :autocomplete-endpoint="locationAutocompleteEndpoint"-->
      <!--          @input="search"-->
      <!--        ></MultiTags>-->
      <!--      </p>-->

      <p>
        <label align="left" :v-bind:for="jobTitleId"></label>Job Title
        <MultiTags
          :key="languageFlag"
          id="popover-button-event"
          v-model="jobTitleId"
          placeholder="Start typing a job title..."
          main_name_key="name"
          secondary_name_key="job_function"
          :autocomplete-endpoint="jobTitleAutocompleteEndpoint"
          v-on:tags-changed="addMockJobFunction($event)"
          @input="search()"
          :max-tags="1"
          :disabled="jobFunctionId"
        ></MultiTags>
      </p>

      <p>
        <label align="left" :v-bind:for="selectedJobFunctionsAndTitles"
          >Job function
          <JobTitlesAndFunctions
            :key="languageFlag"
            :mock-value="mockSelectedJobFunction"
            :disabled="
              !!jobTitleId && !!jobTitleId.length && mockSelectedJobFunction
            "
            v-model="selectedJobFunctionsAndTitles"
            options-source-endpoint="products/job-functions/"
            initialOptionsType="job-functions"
            @input="search()"
          />
        </label>
      </p>

      <p>
        <label align="left" :v-bind:for="industryId">Industries</label>
        <MultiTags
          :key="languageFlag"
          v-model="industryId"
          placeholder="Type an industry..."
          main_name_key="name"
          :autocomplete-items="allIndustries"
          @input="search"
        ></MultiTags>
      </p>
      <p>
        <label
          ><b-form-checkbox v-model="mcEnabled" size="lg" @input="search">
            Only include products with support for My Contracts
          </b-form-checkbox>
        </label>
      </p>
      <p>
        <MultiTags
          :key="languageFlag"
          v-model="industryId"
          placeholder="Type an industry..."
          main_name_key="name"
          :autocomplete-items="allIndustries"
          @input="search"
        ></MultiTags>
      </p>
    </b-form-group>
    <h4>Sort by</h4>
    <p>
      <input
        type="radio"
        v-model="sortBy"
        v-bind:value="'relevant'"
        @change="search"
      />Most relevant first
    </p>
    <p>
      <input
        type="radio"
        v-model="sortBy"
        v-bind:value="'recent'"
        v-on:change="search"
      />Most recently created first
    </p>
    <h4>Recommendations</h4>
    <p>
      <input
        type="radio"
        v-bind:value="'all'"
        v-model="useRecommender"
        v-on:change="search"
      />Display all
    </p>
    <p>
      <input
        type="radio"
        v-bind:value="'recommended'"
        v-model="useRecommender"
        v-on:change="search"
      />Display recommendations only
    </p>
    <p>
      <input
        type="radio"
        v-bind:value="'excludeRecommended'"
        v-model="useRecommender"
        v-on:change="search"
      />Exclude recommendations
    </p>
    <h4>Options</h4>

    Display language
    <b-dropdown
      size="sm"
      variant="link"
      toggle-class="btn btn-icon btn-clean btn-dropdown btn-lg mr-1 text-decoration-none"
      no-caret
      right
      no-flip
    >
      <template v-slot:button-content>
        <img class="h-20px w-20px rounded-sm" :src="languageFlag" alt="" />
      </template>
      <b-dropdown-text tag="div" class="min-w-md-175px">
        <DropdownLanguage
          v-on:language-changed="onLanguageChanged"
        ></DropdownLanguage>
      </b-dropdown-text>
    </b-dropdown>

    <p>Display prices in</p>
    <b-list-group>
      <b-list-group-item
        @click="
          changeCurrency('EUR');
          updateDisplayOptions();
        "
        v-bind:active="displayOptions.currency === 'EUR'"
      >
        EUR
      </b-list-group-item>
      <b-list-group-item
        @click="
          changeCurrency('GBP');
          updateDisplayOptions();
        "
        v-bind:active="displayOptions.currency === 'GBP'"
      >
        GBP
      </b-list-group-item>
      <b-list-group-item
        @click="
          changeCurrency('USD');
          updateDisplayOptions();
        "
        v-bind:active="displayOptions.currency === 'USD'"
      >
        USD
      </b-list-group-item>
      <b-list-group-item
        @click="
          changeCurrency('AUD');
          updateDisplayOptions();
        "
        v-bind:active="displayOptions.currency === 'AUD'"
      >
        AUD
      </b-list-group-item>
    </b-list-group>
  </b-card>
</template>

<script>
import MultiTags from "@/view/content/forms/MultiTags";
import JobTitlesAndFunctions from "@/view/content/forms/JobTitlesAndFunctions";
import DropdownLanguage from "@/view/layout/extras/dropdown/DropdownLanguage";
import i18nService from "@/core/services/i18n.service";
import { HapiService } from "@/core/services/hapi.service";
import CurrencyMixin from "@/view/mixins/CurrencyMixin";

export default {
  name: "SearchBar",
  components: { JobTitlesAndFunctions, MultiTags, DropdownLanguage },
  props: ["displayOptions"],
  mixins: [CurrencyMixin],
  data() {
    return {
      languages: i18nService.languages,
      useRecommender: false,
      useAcceptance: false,
      languageFlag: null,
      locationAutocompleteEndpoint: "products/location/search/",
      jobTitleAutocompleteEndpoint: "products/job-titles/",
      allIndustries: [],
      locationAutocompleteItems: [],
      value: [],

      includeLocationId: [],
      exactLocationId: [],
      jobTitleId: null,
      industryId: [],
      productName: "",
      mcEnabled: false,
      selectedJobFunctionsAndTitles: null,
      mockSelectedJobFunction: null,
      sortBy: "relevant"
    };
  },
  watch: {
    jobFunctionId(newVal, oldval) {
      if (newVal != null && oldval == null) {
        this.onOpen();
        setTimeout(this.onClose, 3000);
      }
      if (newVal == null && oldval != null) {
        this.onClose();
      }
    },
    jobTitleId(newVal, oldval) {
      if (newVal != null && oldval == null) {
        this.onOpen();
        setTimeout(this.onClose, 3000);
      }
      if (newVal == null && oldval != null) {
        this.onClose();
      }
    }
  },
  computed: {
    jobFunctionId() {
      if (
        this.selectedJobFunctionsAndTitles === "" ||
        this.selectedJobFunctionsAndTitles === []
      ) {
        return null;
      }
      return this.selectedJobFunctionsAndTitles;
      // if (this.selectedJobFunctionsAndTitles) {
      //   return parseInt(this.selectedJobFunctionsAndTitles.split(".").pop());
      // }
      // return null;
    },

    searchParams() {
      let params = {};
      if (this.includeLocationId?.length) {
        params.includeLocationId = this.includeLocationId.join(",");
      }
      if (this.industryId?.length) {
        params.industryId = this.industryId.join(",");
      }
      if (this.jobTitleId && !this.jobFunctionId) {
        params["jobTitleId"] = this.jobTitleId.join(",");
      }

      if (this.exactLocationId.length) {
        params["exactLocationId"] = this.exactLocationId.join(",");
      }

      if (this.jobFunctionId) {
        params["jobFunctionId"] = this.jobFunctionId;
      }

      if (this.productName !== "") {
        params["name"] = this.productName;
      }
      if (this.mcEnabled === true) {
        params["mcEnabled"] = this.mcEnabled;
      } else {
        delete params.mcEnabled;
      }

      switch (this.useRecommender) {
        case "recommended":
          params["recommended"] = true;
          break;
        case "excludeRecommended":
          params["excludeRecommended"] = true;
          break;
      }

      params["sortBy"] = this.sortBy;
      return params;
    }
  },

  methods: {
    addMockJobFunction(jobTitles) {
      console.log("calling addMockJobFunction witb ", jobTitles);
      if (!jobTitles || !jobTitles.length || !jobTitles[0].job_function) {
        console.log("no jt in addmockfunction :(");
        this.mockSelectedJobFunction = null;
        return;
      }
      const jobTitle = jobTitles[0];
      this.mockSelectedJobFunction = [jobTitle.job_function.id];
      console.log("adding job function id", this.mockSelectedJobFunction);
    },
    onOpen() {
      this.$refs.popover.$emit("open");
    },
    onClose() {
      this.$refs.popover.$emit("close");
    },
    search() {
      this.$emit("input", this.searchParams);
    },

    onLanguageChanged(language) {
      const i18n = i18nService;
      i18n.setActiveLanguage(language.lang);
      this.languageFlag = this.languages.find(val => {
        console.log("Val in language ", val);
        if (language?.lang?.length > 0) {
          return val.lang === language.lang;
        } else {
          console.log(
            "Does val.lang === i18nService.getActiveLanguage().lang; ? ",
            val.lang === i18n.getActiveLanguage()
          );
          return val.lang === i18n.getActiveLanguage();
        }
      }).flag;
      this.getAllIndustries();
      this.search();
    },

    getAllIndustries() {
      HapiService.listIndustries().then(response => {
        this.allIndustries = response.data;
      });
    }
  },
  async mounted() {
    let language = {};
    // bcs of an old bug, some instances of users' localstorage have activeLanguage set as undefined
    let activeLanguage = i18nService.getActiveLanguage();
    if (activeLanguage === undefined) {
      i18nService.setActiveLanguage(i18nService.defaultLanguage);
      activeLanguage = i18nService.defaultLanguage;
    }

    this.getAllIndustries();
  }
};
</script>

<style scoped></style>
